/**
 * Admin Website page — edit the URLs shown on the public marketing site
 * (zaplayerpro.club/). Empty fields hide their store badge on the Landing page.
 */
import React, { useEffect, useState } from "react";
import { getWebsiteConfig, updateWebsiteConfig } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Globe as GlobeIcon,
  Save,
  ExternalLink,
  RefreshCw,
  Store,
  Download,
  ShieldCheck,
} from "lucide-react";

const STORE_FIELDS = [];

const DIRECT_FIELDS = [
  { key: "direct_apk_url", label: "Direct APK download",  placeholder: "https://zaplayerpro.club/api/downloads/ZA-Player-Pro-Web-1.4.70.apk" },
];

const PURCHASE_FIELDS = [
  { key: "purchase_monthly_url",  label: "Buy — 1 Month plan",   placeholder: "https://buy.stripe.com/... or PayPal link" },
  { key: "purchase_yearly_url",   label: "Buy — 1 Year plan",    placeholder: "https://buy.stripe.com/..." },
  { key: "purchase_lifetime_url", label: "Buy — Lifetime plan",  placeholder: "https://buy.stripe.com/..." },
];

const META_FIELDS = [
  { key: "support_url",   label: "Support page URL",   placeholder: "https://zaplayerpro.club/support" },
  { key: "marketing_url", label: "Marketing home URL", placeholder: "https://zaplayerpro.club" },
  { key: "privacy_url",   label: "Privacy Policy URL", placeholder: "https://zaplayerpro.club/privacy" },
  { key: "terms_url",     label: "Terms of Service URL", placeholder: "https://zaplayerpro.club/terms" },
];

// Contact & branding — surfaced on every public legal page. Changing
// `contact_email` here changes it on the Support form, Landing footer,
// Activation contact link, Privacy/Terms/Refund pages, and the FAQ.
const CONTACT_FIELDS = [
  { key: "contact_email", label: "Support / contact email", placeholder: "support@zaplayerpro.club", type: "email",
    hint: "Used everywhere on the public site: Support page, Landing footer, Activation help link, and inside legal pages. Change once → updates everywhere on the next page load." },
  { key: "privacy_email", label: "Privacy email (optional)", placeholder: "privacy@zaplayerpro.club", type: "email",
    hint: "Where data-access and deletion requests should be sent. Leave blank to auto-derive as privacy@<domain of contact email>." },
  { key: "brand_name",    label: "Brand name", placeholder: "ZA Player Pro",
    hint: "Shown on legal pages, footer copyright line, and the browser tab title." },
  { key: "site_url",      label: "Public site URL", placeholder: "https://zaplayerpro.club",
    hint: "Used in email templates, PDF footers, and the Apple submission PDF." },
];

function FieldRow({ f, value, onChange }) {
  return (
    <div className="grid gap-3 border-b border-white/5 py-3 last:border-b-0 sm:grid-cols-[240px,1fr,44px] sm:items-center">
      <Label htmlFor={f.key} className="text-sm text-neutral-200">
        {f.label}
      </Label>
      <Input
        id={f.key}
        value={value}
        onChange={(e) => onChange(f.key, e.target.value)}
        placeholder={f.placeholder}
        className="border-white/10 bg-neutral-950 text-sm"
        data-testid={`website-input-${f.key}`}
      />
      {value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:border-emerald-400/40 hover:text-emerald-400"
          title="Open link"
          data-testid={`website-open-${f.key}`}
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      ) : (
        <div />
      )}
    </div>
  );
}

export default function Website() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getWebsiteConfig();
      setCfg(data);
      setInitial(data);
    } catch (e) {
      toast({
        title: "Failed to load website config",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = cfg && initial && JSON.stringify(cfg) !== JSON.stringify(initial);

  const setField = (k, v) => setCfg((c) => ({ ...(c || {}), [k]: v }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const updated = await updateWebsiteConfig(cfg);
      setCfg(updated);
      setInitial(updated);
      toast({
        title: "Website updated",
        description: "The public landing page now uses these links.",
      });
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setCfg(initial);

  if (loading || !cfg) {
    return <div className="p-6 text-neutral-500" data-testid="website-loading">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-website-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            <GlobeIcon className="h-7 w-7 text-emerald-400" />
            Website
          </h1>
          <p className="max-w-2xl text-sm text-neutral-400">
            Edit the app-store, direct-download and legal URLs shown on
            the public landing page at{" "}
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-emerald-400 underline"
              data-testid="website-view-live"
            >
              zaplayerpro.club/
            </a>
            . Blank fields are hidden on the site.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={load}
            disabled={saving}
            data-testid="website-refresh-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button
            variant="outline"
            onClick={revert}
            disabled={!dirty || saving}
            data-testid="website-revert-btn"
          >
            Revert
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="website-save-btn"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* App-store links */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Store className="h-5 w-5 text-emerald-400" /> App-store links
          </CardTitle>
          <CardDescription className="text-xs text-neutral-500">
            Each URL renders as a store badge in the &ldquo;Available on every screen you own&rdquo; section.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {STORE_FIELDS.map((f) => (
            <FieldRow key={f.key} f={f} value={cfg[f.key] || ""} onChange={setField} />
          ))}
        </CardContent>
      </Card>

      {/* Direct downloads */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Download className="h-5 w-5 text-emerald-400" /> Direct downloads
          </CardTitle>
          <CardDescription className="text-xs text-neutral-500">
            Tiles below the store grid. Fill only the tiles you want to expose publicly.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {DIRECT_FIELDS.map((f) => (
            <FieldRow key={f.key} f={f} value={cfg[f.key] || ""} onChange={setField} />
          ))}
        </CardContent>
      </Card>

      {/* Purchase links (Activation page) */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ExternalLink className="h-5 w-5 text-emerald-400" /> Purchase links
          </CardTitle>
          <CardDescription className="text-xs text-neutral-500">
            Displayed on the public {`/activation`} page. Blank plans fall back to a mailto&nbsp;support link.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {PURCHASE_FIELDS.map((f) => (
            <FieldRow key={f.key} f={f} value={cfg[f.key] || ""} onChange={setField} />
          ))}
        </CardContent>
      </Card>

      {/* Contact & branding — the single source of truth for every
          public-facing email address, brand name, and site URL. */}
      <Card className="border-white/10 bg-neutral-900" data-testid="contact-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-emerald-400" /> Contact &amp; branding
          </CardTitle>
          <CardDescription className="text-xs text-neutral-500">
            Change once — the new value shows up on the Support page, Landing
            footer, Activation help link, and inside every legal page
            (Privacy, Terms, Refund) on the next page load.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {CONTACT_FIELDS.map((f) => (
            <div key={f.key} className="border-b border-white/5 py-3 last:border-b-0">
              <FieldRow f={f} value={cfg[f.key] || ""} onChange={setField} />
              {f.hint && (
                <p className="ml-0 mt-1 text-[11px] leading-relaxed text-neutral-500 sm:ml-[240px]">
                  {f.hint}
                </p>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Meta / legal */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-emerald-400" /> Legal page URLs
          </CardTitle>
          <CardDescription className="text-xs text-neutral-500">
            Referenced from the footer and required by app-store reviewers.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {META_FIELDS.map((f) => (
            <FieldRow key={f.key} f={f} value={cfg[f.key] || ""} onChange={setField} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
